/**
 * Clearance zone vs room walls check.
 *
 * Treats each non-parent wall as a finite-length, outward-extruded OBB and
 * runs SAT against the fixture's clearance polygon. Mirrors the
 * physical-boundary check, just using clearance corners. Each collision
 * carries the specific blocking wall, so the snap handler can compute MTV
 * directly against that wall.
 */

const { getObjectClearanceFootprints } = require('./geometry');
const { getClearanceConfig } = require('./clearanceConfig');
const { FLOORPLAN } = require('./tolerances');
const {
  overlapAmount,
} = require('../bathroom-analyzer/pipeline/5.1-physicalCollisions');
const {
  segmentToBoundaryObb,
  standingWallSegments,
} = require('./wallBoundary');
const { isDemoedOpening } = require('../openings/openingSync');

/**
 * Get the room floor boundary polygon from room data.
 * Uses the first floor's polygonCorners if available.
 *
 * @param {Object} roomData - { floors }
 * @returns {Object[]|null} Array of {x, z} points, or null
 */
function getRoomBoundary(roomData) {
  const floor = (roomData.floors || [])[0];
  if (!floor?.polygonCorners?.length) return null;

  return floor.polygonCorners.map((c) => ({
    x: c.x,
    z: c.z,
  }));
}

/**
 * Check if a fixture's clearance zone overlaps any non-parent wall.
 *
 * The clearance polygon is SAT-tested against each non-parent wall's
 * outward-extruded OBB. Wall thickness comes from
 * `getWallBoundaryThickness` (the wall's real thickness, with a 1mm floor
 * to keep SAT well-conditioned), shared with `checkPhysicalBoundary`.
 *
 * Replaces the prior point-in-polygon check on front corners only. The
 * special-case "front corners only" rule is no longer needed: a clearance
 * polygon flush against the parent wall has zero overlap with the parent
 * wall's OBB (touching = 0), and the parent is excluded from the obstacle
 * list anyway, so on-wall back corners don't trigger false positives.
 *
 * @param {Object} object - The fixture being placed
 * @param {Object} roomData - { walls, ... }
 * @param {Object} [attachedWall] - The wall this fixture is mounted/snapped to
 * @returns {Array<{type, target, reason}>}
 */
function checkClearanceBoundary(object, roomData, attachedWall = null) {
  const collisions = [];
  const config = getClearanceConfig(object);

  const walls = roomData.walls || [];
  if (walls.length === 0) return collisions;

  // Gates on footprints rather than config.frontClearance so cabinets
  // (frontClearance: 0, but per-sink footprints) participate.
  const footprints = getObjectClearanceFootprints(
    object,
    config,
    roomData.objects || []
  );
  if (footprints.length === 0) return collisions;

  const skipWallIds = new Set();
  if (attachedWall?.id) skipWallIds.add(attachedWall.id);
  if (Array.isArray(object.parentWallIds)) {
    for (const wid of object.parentWallIds) skipWallIds.add(wid);
  }

  // Corner tubs: also skip adjacent perpendicular walls (same logic as
  // physicalBoundary.js — see comment there for rationale).
  const objType = (object.type || '').toLowerCase();
  const isCornerTub =
    (objType === 'bathtub' || objType === 'tub-support-box') &&
    object['tub-surround-polygon']?.points?.length >= 5;
  if (isCornerTub && skipWallIds.size > 0) {
    const parentWalls = walls.filter((w) => skipWallIds.has(w.id));
    for (const pw of parentWalls) {
      const pwStart = pw.start || pw.alignment?.bottomLeft;
      const pwEnd = pw.end || pw.alignment?.bottomRight;
      if (!pwStart || !pwEnd) continue;
      const pwDx = pwEnd.x - pwStart.x;
      const pwDz = pwEnd.z - pwStart.z;
      const pwLen = Math.sqrt(pwDx * pwDx + pwDz * pwDz);
      if (pwLen < 1e-10) continue;

      for (const w of walls) {
        if (skipWallIds.has(w.id)) continue;
        const wStart = w.start || w.alignment?.bottomLeft;
        const wEnd = w.end || w.alignment?.bottomRight;
        if (!wStart || !wEnd) continue;

        const ENDPOINT_TOL = 0.05;
        const d1 = Math.hypot(pwStart.x - wStart.x, pwStart.z - wStart.z);
        const d2 = Math.hypot(pwStart.x - wEnd.x, pwStart.z - wEnd.z);
        const d3 = Math.hypot(pwEnd.x - wStart.x, pwEnd.z - wStart.z);
        const d4 = Math.hypot(pwEnd.x - wEnd.x, pwEnd.z - wEnd.z);
        if (
          d1 > ENDPOINT_TOL &&
          d2 > ENDPOINT_TOL &&
          d3 > ENDPOINT_TOL &&
          d4 > ENDPOINT_TOL
        )
          continue;

        const wDx = wEnd.x - wStart.x;
        const wDz = wEnd.z - wStart.z;
        const wLen = Math.sqrt(wDx * wDx + wDz * wDz);
        if (wLen < 1e-10) continue;
        const dot = Math.abs(pwDx * wDx + pwDz * wDz) / (pwLen * wLen);
        if (dot < 0.35) {
          skipWallIds.add(w.id);
        }
      }
    }
  }

  const demoOpenings = (roomData.openings || []).filter((o) =>
    isDemoedOpening(o)
  );

  for (const wall of walls) {
    if (skipWallIds.has(wall.id)) continue;
    // A virtual boundary (a kitchen-area / region edge no structure wall
    // backs) is not an obstacle — see `markVirtualBoundaryWalls`.
    if (wall.virtual === true) continue;

    // Test the clearance polygon against each STANDING sub-segment so a
    // clearance zone projecting through a demolished gap isn't flagged.
    let worstOverlap = 0;
    for (const seg of standingWallSegments(wall, demoOpenings)) {
      const obb = segmentToBoundaryObb(seg.start, seg.end, wall);
      if (!obb) continue;
      for (const fp of footprints) {
        if (!fp || fp.length < 4) continue;
        const overlap = overlapAmount(fp, obb, { tolerance: FLOORPLAN });
        if (overlap > worstOverlap) worstOverlap = overlap;
      }
    }
    if (worstOverlap > 0) {
      collisions.push({
        type: 'clearance-boundary',
        target: { id: wall.id, type: 'wall', wall },
        reason: `Clearance zone overlaps wall ${wall.id} by ${(worstOverlap * 1000).toFixed(0)}mm`,
      });
    }
  }

  return collisions;
}

module.exports = {
  checkClearanceBoundary,
  getRoomBoundary,
};
